import {
  TranscriptSpeakerAudioSource,
  TranscriptSpeakerIdentificationContext,
  TranscriptSpeakerIdentificationReason,
  TranscriptSpeakerVariant,
  type TranscriptSpeakerParticipantMetadata,
} from "@/modules/audio-recording/transcript-types";
import {
  isRecallNonIdentityParticipantName,
  normalizeSpeakerParticipantName,
  resolveTranscriptSpeakerDisplayLabel as resolveParticipantTranscriptSpeakerDisplayLabel,
  resolveTranscriptSpeakerGroupKey as resolveParticipantTranscriptSpeakerGroupKey,
} from "@/modules/audio-recording/transcript-speaker-labels";

export type TranscriptSpeakerIdentification = {
  speakerId: string | null;
  speakerVariant: TranscriptSpeakerVariant;
  speakerAudioSource: TranscriptSpeakerAudioSource;
  speakerIdentificationContext: TranscriptSpeakerIdentificationContext;
  speakerIdentificationReason: TranscriptSpeakerIdentificationReason;
};

export type TranscriptSpeakerPresentation = {
  speakerVariant: TranscriptSpeakerVariant;
  displayLabel: string;
  isSelfSpeaker: boolean;
  isTranscriptOnly: boolean;
  groupKey: string;
};

/**
 * User identity evidence available to the transcript presentation layer.
 *
 * Meeting-window recordings report every platform participant as a participant,
 * including the signed-in user, so we only use stable account fields to recover
 * the "me" bubble locally.
 */
export type TranscriptSpeakerCurrentUser = {
  email?: string | null;
  displayName?: string | null;
  allowUniqueDisplayNameFallback?: boolean;
  uniqueDisplayNameParticipantKey?: string | null;
};

type TranscriptSpeakerParticipantCandidate = {
  speakerId?: string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
};

const UNSAFE_DISPLAY_NAME_FALLBACKS = new Set([
  "account",
  "admin",
  "host",
  "guest",
  "me",
  "member",
  "owner",
  "participant",
  "teams user",
  "unknown",
  "user",
  "webex user",
  "you",
  "zoom user",
]);

export function parseTranscriptSpeakerAudioSource(value: unknown): TranscriptSpeakerAudioSource {
  switch (value) {
    case TranscriptSpeakerAudioSource.LocalMicrophone:
    case TranscriptSpeakerAudioSource.SystemAudio:
    case TranscriptSpeakerAudioSource.Unknown:
      return value;
    default:
      return TranscriptSpeakerAudioSource.Unknown;
  }
}

export function parseOptionalTranscriptSpeakerVariant(
  value: unknown
): TranscriptSpeakerVariant | undefined {
  if (value == null) return undefined;
  if (typeof value !== "string") return TranscriptSpeakerVariant.Unknown;

  switch (value) {
    case TranscriptSpeakerVariant.Self:
    case TranscriptSpeakerVariant.Other:
    case TranscriptSpeakerVariant.Unknown:
      return value as TranscriptSpeakerVariant;
    default:
      return TranscriptSpeakerVariant.Unknown;
  }
}

export function parseTranscriptSpeakerIdentificationContext(
  value: unknown
): TranscriptSpeakerIdentificationContext {
  switch (value) {
    case TranscriptSpeakerIdentificationContext.WholeDesktop:
    case TranscriptSpeakerIdentificationContext.MeetingWindow:
    case TranscriptSpeakerIdentificationContext.Unknown:
      return value;
    default:
      return TranscriptSpeakerIdentificationContext.Unknown;
  }
}

export function parseTranscriptSpeakerIdentificationReason(
  value: unknown
): TranscriptSpeakerIdentificationReason {
  if (typeof value === "string") {
    const legacyReasonAliases: Record<string, TranscriptSpeakerIdentificationReason> = {
      whole_desktop_local_microphone:
        TranscriptSpeakerIdentificationReason.WholeDesktopRecallHostLabel,
      whole_desktop_other_audio: TranscriptSpeakerIdentificationReason.WholeDesktopRecallGuestLabel,
      whole_desktop_unknown_audio: TranscriptSpeakerIdentificationReason.WholeDesktopUnknown,
      meeting_window_local_microphone: TranscriptSpeakerIdentificationReason.LegacyChannel,
      meeting_window_system_audio: TranscriptSpeakerIdentificationReason.LegacyChannel,
      meeting_window_unknown_audio: TranscriptSpeakerIdentificationReason.MeetingWindowUnknown,
    };
    const legacyReason = legacyReasonAliases[value.trim().toLowerCase()];
    if (legacyReason) return legacyReason;
  }

  switch (value) {
    case TranscriptSpeakerIdentificationReason.LegacyChannel:
    case TranscriptSpeakerIdentificationReason.WholeDesktopRecallHostLabel:
    case TranscriptSpeakerIdentificationReason.WholeDesktopRecallGuestLabel:
    case TranscriptSpeakerIdentificationReason.WholeDesktopUnknown:
    case TranscriptSpeakerIdentificationReason.MeetingWindowPlatformParticipant:
    case TranscriptSpeakerIdentificationReason.MeetingWindowUnknown:
    case TranscriptSpeakerIdentificationReason.Unknown:
      return value;
    default:
      return TranscriptSpeakerIdentificationReason.Unknown;
  }
}

export function resolveTranscriptSpeakerIdentification({
  context,
  recallSpeakerLabel,
  speakerParticipant,
  participantId,
}: {
  context: TranscriptSpeakerIdentificationContext;
  recallSpeakerLabel?: string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  participantId?: number | string | null;
}): TranscriptSpeakerIdentification {
  const normalizedContext = parseTranscriptSpeakerIdentificationContext(context);

  if (normalizedContext === TranscriptSpeakerIdentificationContext.WholeDesktop) {
    return resolveWholeDesktopSpeakerIdentification({
      recallSpeakerLabel,
      speakerParticipant,
    });
  }

  if (normalizedContext === TranscriptSpeakerIdentificationContext.MeetingWindow) {
    return resolveMeetingWindowSpeakerIdentification({
      speakerParticipant,
      participantId,
    });
  }

  return resolveUnknownContextSpeakerIdentification();
}

export function resolveTranscriptChannelFromSpeakerVariant({
  speakerVariant,
}: {
  speakerVariant: TranscriptSpeakerVariant;
}): 0 | 1 {
  return speakerVariant === TranscriptSpeakerVariant.Self ? 0 : 1;
}

export function resolveTranscriptSpeakerVariant({
  speakerVariant,
  channel,
}: {
  speakerVariant?: TranscriptSpeakerVariant | null;
  channel?: number | null;
}): TranscriptSpeakerVariant {
  const parsedVariant = parseOptionalTranscriptSpeakerVariant(speakerVariant);
  if (parsedVariant) return parsedVariant;

  if (channel === 0) return TranscriptSpeakerVariant.Self;
  if (typeof channel === "number" && channel > 0) return TranscriptSpeakerVariant.Other;
  return TranscriptSpeakerVariant.Unknown;
}

export function resolveTranscriptSpeakerPresentation({
  speakerVariant,
  channel,
  speakerParticipant,
  speakerId,
  currentUser,
}: {
  speakerVariant?: TranscriptSpeakerVariant | null;
  channel?: number | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  speakerId?: string | null;
  currentUser?: TranscriptSpeakerCurrentUser | null;
}): TranscriptSpeakerPresentation {
  const isTranscriptOnly = isLegacyTranscriptOnlyChannel(channel);
  const baseVariant = resolveTranscriptSpeakerVariant({ speakerVariant, channel });
  const resolvedVariant =
    !isTranscriptOnly &&
    isTranscriptSpeakerCurrentUser({
      speakerParticipant,
      speakerId,
      currentUser,
    })
      ? TranscriptSpeakerVariant.Self
      : baseVariant;
  const participantDisplayLabel = resolveTranscriptSpeakerParticipantDisplayLabel({
    speakerParticipant,
  });

  return {
    speakerVariant: resolvedVariant,
    displayLabel: isTranscriptOnly
      ? "Transcript"
      : resolvedVariant === TranscriptSpeakerVariant.Other && participantDisplayLabel
        ? participantDisplayLabel
        : resolveTranscriptSpeakerDisplayLabelFromVariant(resolvedVariant),
    isSelfSpeaker: resolvedVariant === TranscriptSpeakerVariant.Self,
    isTranscriptOnly,
    groupKey: resolveTranscriptSpeakerPresentationGroupKey({
      channel,
      speakerId,
      speakerParticipant,
      speakerVariant: resolvedVariant,
      isTranscriptOnly,
    }),
  };
}

export function resolveTranscriptSpeakerDisplayLabel({
  speakerVariant,
  channel,
  speakerParticipant,
  speakerId,
  currentUser,
}: {
  speakerVariant?: TranscriptSpeakerVariant | null;
  channel?: number | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  speakerId?: string | null;
  currentUser?: TranscriptSpeakerCurrentUser | null;
}): string {
  return resolveTranscriptSpeakerPresentation({
    speakerVariant,
    channel,
    speakerParticipant,
    speakerId,
    currentUser,
  }).displayLabel;
}

/**
 * Returns a participant label only when Recall supplied human identity metadata.
 *
 * Placeholder names such as "Host", "Guest", and diarization-only labels are
 * filtered by the shared transcript label helper so live bubbles and persisted
 * transcript text agree on what is displayable.
 */
export function resolveTranscriptSpeakerParticipantDisplayLabel({
  speakerParticipant,
  includeEmail = false,
}: {
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  includeEmail?: boolean;
}): string | null {
  const participantLabel = resolveParticipantTranscriptSpeakerDisplayLabel({
    speakerParticipant,
    includeParticipantEmail: includeEmail,
  });

  return participantLabel.source === "participant" ? participantLabel.label : null;
}

export function resolveTranscriptSpeakerCurrentUserContext({
  currentUser,
  speakers,
}: {
  currentUser?: TranscriptSpeakerCurrentUser | null;
  speakers: TranscriptSpeakerParticipantCandidate[];
}): TranscriptSpeakerCurrentUser | null {
  if (!currentUser) return null;

  return {
    ...currentUser,
    uniqueDisplayNameParticipantKey: currentUser.allowUniqueDisplayNameFallback
      ? resolveUniqueDisplayNameParticipantKey({
          currentUser,
          speakers,
        })
      : null,
  };
}

function resolveWholeDesktopSpeakerIdentification({
  recallSpeakerLabel,
  speakerParticipant,
}: {
  recallSpeakerLabel?: string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
}): TranscriptSpeakerIdentification {
  const context = TranscriptSpeakerIdentificationContext.WholeDesktop;
  const wholeDesktopLabelVariant = resolveWholeDesktopVendorLabelVariant({
    recallSpeakerLabel,
    speakerParticipant,
  });

  if (wholeDesktopLabelVariant === TranscriptSpeakerVariant.Self) {
    return {
      speakerId: "whole_desktop:local_microphone",
      speakerVariant: TranscriptSpeakerVariant.Self,
      speakerAudioSource: TranscriptSpeakerAudioSource.LocalMicrophone,
      speakerIdentificationContext: context,
      speakerIdentificationReason:
        TranscriptSpeakerIdentificationReason.WholeDesktopRecallHostLabel,
    };
  }

  if (wholeDesktopLabelVariant === TranscriptSpeakerVariant.Other) {
    return {
      speakerId: "whole_desktop:other_audio",
      speakerVariant: TranscriptSpeakerVariant.Other,
      speakerAudioSource: TranscriptSpeakerAudioSource.SystemAudio,
      speakerIdentificationContext: context,
      speakerIdentificationReason:
        TranscriptSpeakerIdentificationReason.WholeDesktopRecallGuestLabel,
    };
  }

  return {
    speakerId: null,
    speakerVariant: TranscriptSpeakerVariant.Unknown,
    speakerAudioSource: TranscriptSpeakerAudioSource.Unknown,
    speakerIdentificationContext: context,
    speakerIdentificationReason: TranscriptSpeakerIdentificationReason.WholeDesktopUnknown,
  };
}

function resolveMeetingWindowSpeakerIdentification({
  speakerParticipant,
  participantId,
}: {
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  participantId?: number | string | null;
}): TranscriptSpeakerIdentification {
  const context = TranscriptSpeakerIdentificationContext.MeetingWindow;
  const participantSpeakerId = resolveParticipantSpeakerId({
    context,
    speakerParticipant,
    participantId,
  });

  if (participantSpeakerId || hasSpeakerParticipantMetadata(speakerParticipant)) {
    return {
      speakerId: participantSpeakerId,
      speakerVariant: TranscriptSpeakerVariant.Other,
      speakerAudioSource: TranscriptSpeakerAudioSource.Unknown,
      speakerIdentificationContext: context,
      speakerIdentificationReason:
        TranscriptSpeakerIdentificationReason.MeetingWindowPlatformParticipant,
    };
  }

  return {
    speakerId: null,
    speakerVariant: TranscriptSpeakerVariant.Unknown,
    speakerAudioSource: TranscriptSpeakerAudioSource.Unknown,
    speakerIdentificationContext: context,
    speakerIdentificationReason: TranscriptSpeakerIdentificationReason.MeetingWindowUnknown,
  };
}

function resolveUnknownContextSpeakerIdentification(): TranscriptSpeakerIdentification {
  return {
    speakerId: null,
    speakerVariant: TranscriptSpeakerVariant.Unknown,
    speakerAudioSource: TranscriptSpeakerAudioSource.Unknown,
    speakerIdentificationContext: TranscriptSpeakerIdentificationContext.Unknown,
    speakerIdentificationReason: TranscriptSpeakerIdentificationReason.Unknown,
  };
}

function resolveWholeDesktopVendorLabelVariant({
  recallSpeakerLabel,
  speakerParticipant,
}: {
  recallSpeakerLabel?: string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
}): TranscriptSpeakerVariant | null {
  const rawLabel = normalizeSpeakerParticipantName(
    recallSpeakerLabel ??
      speakerParticipant?.extraData?.recall_speaker_label ??
      speakerParticipant?.name
  )?.toLowerCase();
  if (rawLabel === "host") return TranscriptSpeakerVariant.Self;
  if (rawLabel === "guest") return TranscriptSpeakerVariant.Other;

  return null;
}

function resolveParticipantSpeakerId({
  context,
  speakerParticipant,
  participantId,
}: {
  context: TranscriptSpeakerIdentificationContext;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  participantId?: number | string | null;
}): string | null {
  const normalizedParticipantId = normalizeSpeakerIdentifier(
    speakerParticipant?.id ?? participantId
  );
  if (!normalizedParticipantId) return null;

  return `${context}:participant:${normalizedParticipantId}`;
}

function normalizeSpeakerIdentifier(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return normalizeSpeakerParticipantName(value) ?? null;
}

function hasSpeakerParticipantMetadata(
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null
): boolean {
  if (!speakerParticipant) return false;

  return (
    speakerParticipant.id != null ||
    Boolean(normalizeSpeakerParticipantName(speakerParticipant.name)) ||
    Boolean(normalizeSpeakerParticipantName(speakerParticipant.email))
  );
}

function isLegacyTranscriptOnlyChannel(channel?: number | null): boolean {
  return typeof channel === "number" && channel < 0;
}

/**
 * Matches a transcript participant to the signed-in user without trusting
 * platform host roles.
 *
 * Email is the strongest self evidence. Meeting display names can be duplicated
 * or generic, so name matches only apply when the caller opts into the stricter
 * unique full-name fallback.
 */
function isTranscriptSpeakerCurrentUser({
  speakerParticipant,
  speakerId,
  currentUser,
}: {
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  speakerId?: string | null;
  currentUser?: TranscriptSpeakerCurrentUser | null;
}): boolean {
  if (!speakerParticipant || !currentUser) return false;

  const participantEmail = normalizeEmailForComparison(speakerParticipant.email);
  const currentUserEmail = normalizeEmailForComparison(currentUser.email);
  if (participantEmail) {
    return Boolean(currentUserEmail && participantEmail === currentUserEmail);
  }

  const uniqueDisplayNameParticipantKey = currentUser.uniqueDisplayNameParticipantKey;
  if (!uniqueDisplayNameParticipantKey) return false;

  const currentUserDisplayName = normalizeDisplayNameForCurrentUserFallback(
    currentUser.displayName
  );
  const participantDisplayName = normalizeDisplayNameForCurrentUserFallback(
    speakerParticipant.name
  );
  if (!currentUserDisplayName || currentUserDisplayName !== participantDisplayName) {
    return false;
  }

  return (
    resolveStableSpeakerParticipantKey({
      speakerId,
      speakerParticipant,
    }) === uniqueDisplayNameParticipantKey
  );
}

/**
 * Lets presentation recover "me" bubbles when Recall omits participant email.
 *
 * This is intentionally stricter than general display-name matching: it only
 * applies to surfaces that opt in, requires a non-generic full display-name
 * match, ignores participants with non-matching emails, and needs exactly one
 * stable participant id for that name in the rendered transcript.
 */
function resolveUniqueDisplayNameParticipantKey({
  currentUser,
  speakers,
}: {
  currentUser: TranscriptSpeakerCurrentUser;
  speakers: TranscriptSpeakerParticipantCandidate[];
}): string | null {
  const currentUserDisplayName = normalizeDisplayNameForCurrentUserFallback(
    currentUser.displayName
  );
  if (!currentUserDisplayName) return null;

  const candidateKeys = new Set<string>();

  for (const { speakerId, speakerParticipant } of speakers) {
    if (!speakerParticipant) continue;
    if (normalizeEmailForComparison(speakerParticipant.email)) continue;

    const participantDisplayName = normalizeDisplayNameForCurrentUserFallback(
      speakerParticipant.name
    );
    if (participantDisplayName !== currentUserDisplayName) continue;

    const stableParticipantKey = resolveStableSpeakerParticipantKey({
      speakerId,
      speakerParticipant,
    });
    if (!stableParticipantKey) return null;

    candidateKeys.add(stableParticipantKey);
  }

  return candidateKeys.size === 1 ? [...candidateKeys][0] : null;
}

function normalizeDisplayNameForCurrentUserFallback(value: unknown): string | null {
  const name = normalizeSpeakerParticipantName(value);
  if (!name || isRecallNonIdentityParticipantName(name)) return null;

  const normalized = name.toLowerCase().replace(/\s+/g, " ").trim();
  if (UNSAFE_DISPLAY_NAME_FALLBACKS.has(normalized)) return null;
  if (normalized.split(" ").length < 2) return null;

  return normalized;
}

function resolveStableSpeakerParticipantKey({
  speakerId,
  speakerParticipant,
}: TranscriptSpeakerParticipantCandidate): string | null {
  const participantId = normalizeSpeakerIdentifier(speakerParticipant?.id);
  if (participantId) return `participant-id:${participantId}`;

  const participantEmail = normalizeEmailForComparison(speakerParticipant?.email);
  if (participantEmail) return `participant-email:${participantEmail}`;

  const stableSpeakerId = normalizeSpeakerIdentifier(speakerId);
  if (stableSpeakerId) return `speaker-id:${stableSpeakerId}`;

  return null;
}

/**
 * Adds participant-specific grouping when Recall gives enough identity metadata.
 *
 * Generic other-speaker turns keep the historical group keys so old channel-only
 * transcripts still coalesce exactly as before.
 */
function resolveTranscriptSpeakerPresentationGroupKey({
  channel,
  speakerId,
  speakerParticipant,
  speakerVariant,
  isTranscriptOnly,
}: {
  channel?: number | null;
  speakerId?: string | null;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata | null;
  speakerVariant: TranscriptSpeakerVariant;
  isTranscriptOnly: boolean;
}): string {
  const groupKind = isTranscriptOnly ? "transcript" : "speaker";
  const baseGroupKey = `${groupKind}:${speakerVariant}`;

  if (isTranscriptOnly || speakerVariant === TranscriptSpeakerVariant.Self) {
    return baseGroupKey;
  }

  const participantSpecificKey = normalizeSpeakerIdentifier(speakerId);
  if (participantSpecificKey) return `${baseGroupKey}:${participantSpecificKey}`;

  if (!hasSpeakerParticipantMetadata(speakerParticipant)) return baseGroupKey;

  const participantGroupKey = resolveParticipantTranscriptSpeakerGroupKey({
    channel,
    speakerParticipant,
  });
  if (participantGroupKey.startsWith("channel:")) return baseGroupKey;

  return `${baseGroupKey}:${participantGroupKey}`;
}

function normalizeEmailForComparison(value: unknown): string | null {
  return normalizeSpeakerParticipantName(value)?.toLowerCase() ?? null;
}

function resolveTranscriptSpeakerDisplayLabelFromVariant(
  speakerVariant: TranscriptSpeakerVariant
): string {
  if (speakerVariant === TranscriptSpeakerVariant.Self) return "Me";
  if (speakerVariant === TranscriptSpeakerVariant.Other) return "Other Speakers";
  return "Unknown Speaker";
}
